/*
 * Shared premium UI primitives for the 2FA flows (challenge + enrolment).
 *
 *   - OtpInput            segmented [ ][ ][ ] - [ ][ ][ ] code field
 *   - CountdownRing       animated circular 60s timer that flips to "Resend"
 *   - RememberDeviceRow   "Remember this device" checkbox row
 *   - TwoFactorBrand      gradient shield header artwork
 *
 * Happy Premium look: brand orange (`#F97316`), soft tinted fills, rounded 16
 * code boxes with an orange focus/filled state, white glyphs on orange.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    Animated,
    Easing,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

export const COLORS = {
    brand: '#F97316',
    brandDark: '#EA580C',
    tint: '#FFF7ED',
    ink: '#0F172A',
    sub: '#64748B',
    line: '#E2E8F0',
    soft: '#F8FAFC',
    success: '#16A34A',
    danger: '#DC2626',
};

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

/* ───────────────────────── Segmented OTP input ──────────────────────────── */
export function OtpInput({
    value = '',
    onChange,
    length = 6,
    groupBy = 3,
    autoFocus = true,
    disabled = false,
    onComplete,
}) {
    const refs = useRef([]);
    const [focused, setFocused] = useState(-1);
    const chars = String(value || '').padEnd(length, ' ').slice(0, length).split('');

    const commit = (next) => {
        const clean = next.join('').replace(/\s/g, '');

        onChange?.(clean);
        if (clean.length === length) {
            onComplete?.(clean);
        }
    };

    const setChar = (idx, ch) => {
        const next = String(value || '').padEnd(length, ' ').split('');

        next[idx] = ch || ' ';
        commit(next);
    };

    const handleChange = (idx, text) => {
        const digits = String(text || '').replace(/\D/g, '');

        if (digits.length > 1) {
            // Paste / autofill — distribute across the boxes.
            const next = String(value || '').padEnd(length, ' ').split('');

            for (let i = 0; i < digits.length && idx + i < length; i += 1) {
                next[idx + i] = digits[i];
            }
            commit(next);
            refs.current[Math.min(idx + digits.length, length - 1)]?.focus();

            return;
        }
        setChar(idx, digits);
        if (digits && idx < length - 1) {
            refs.current[idx + 1]?.focus();
        }
    };

    const handleKey = (idx, key) => {
        if (key === 'Backspace' && !chars[idx].trim() && idx > 0) {
            refs.current[idx - 1]?.focus();
            setChar(idx - 1, '');
        }
    };

    return (
        <View style={styles.otpRow}>
            {Array.from({ length }, (_, i) => (
                <React.Fragment key={i}>
                    {i > 0 && i % groupBy === 0 && <View style={styles.otpDash} />}
                    <TextInput
                        ref={(el) => { refs.current[i] = el; }}
                        value={chars[i].trim()}
                        editable={!disabled}
                        autoFocus={autoFocus && i === 0}
                        keyboardType="number-pad"
                        textContentType="oneTimeCode"
                        autoComplete="sms-otp"
                        maxLength={i === 0 ? length : 1}
                        onChangeText={(text) => handleChange(i, text)}
                        onKeyPress={({ nativeEvent }) => handleKey(i, nativeEvent.key)}
                        onFocus={() => setFocused(i)}
                        onBlur={() => setFocused((current) => (current === i ? -1 : current))}
                        style={[
                            styles.otpBox,
                            chars[i].trim() ? styles.otpBoxFilled : null,
                            focused === i ? styles.otpBoxFocused : null,
                            disabled ? styles.otpBoxDisabled : null,
                        ]}
                    />
                </React.Fragment>
            ))}
        </View>
    );
}

/* ───────────────────────── Animated countdown ring ──────────────────────── */
export function CountdownRing({
    seconds = 60,
    runKey = 0,
    onExpire,
    onResend,
    resendLabel = 'Resend',
    size = 76,
    stroke = 6,
}) {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = useRef(new Animated.Value(0)).current;
    const [remaining, setRemaining] = useState(seconds);
    const [expired, setExpired] = useState(false);

    useEffect(() => {
        setExpired(false);
        setRemaining(seconds);
        progress.setValue(0);

        const anim = Animated.timing(progress, {
            toValue: 1,
            duration: seconds * 1000,
            easing: Easing.linear,
            useNativeDriver: true,
        });

        anim.start();

        const started = Date.now();
        const interval = setInterval(() => {
            const left = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));

            setRemaining(left);
            if (left <= 0) {
                clearInterval(interval);
                setExpired(true);
                onExpire?.();
            }
        }, 250);

        return () => {
            clearInterval(interval);
            anim.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runKey, seconds]);

    const strokeDashoffset = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, circumference],
    });

    if (expired) {
        return (
            <TouchableOpacity style={styles.resendBtn} onPress={onResend} activeOpacity={0.85}>
                <Ionicons name="refresh" size={16} color={COLORS.brandDark} />
                <Text style={styles.resendText}>{resendLabel}</Text>
            </TouchableOpacity>
        );
    }

    return (
        <View style={{ width: size, height: size }}>
            <Svg width={size} height={size}>
                <Circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={COLORS.line}
                    strokeWidth={stroke}
                    fill="none"
                />
                <AnimatedCircle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={COLORS.brand}
                    strokeWidth={stroke}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={strokeDashoffset}
                    rotation="-90"
                    origin={`${size / 2}, ${size / 2}`}
                />
            </Svg>
            <View style={styles.ringCenter}>
                <Text style={styles.ringNumber}>{remaining}</Text>
                <Text style={styles.ringUnit}>s</Text>
            </View>
        </View>
    );
}

/* ───────────────────────── Remember device checkbox ─────────────────────── */
export function RememberDeviceRow({ value, onToggle, title, subtitle }) {
    return (
        <TouchableOpacity style={styles.rememberRow} onPress={() => onToggle(!value)} activeOpacity={0.8}>
            <View style={[styles.checkbox, value ? styles.checkboxOn : null]}>
                {value && <Ionicons name="checkmark" size={15} color="#fff" />}
            </View>
            <View style={{ flex: 1 }}>
                <Text style={styles.rememberTitle}>{title}</Text>
                {!!subtitle && <Text style={styles.rememberSub}>{subtitle}</Text>}
            </View>
        </TouchableOpacity>
    );
}

/* ───────────────────────── Gradient shield header ───────────────────────── */
export function TwoFactorBrand({ icon = 'shield-checkmark', size = 64 }) {
    return (
        <LinearGradient
            colors={[COLORS.brand, COLORS.brandDark]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.brand, { width: size, height: size, borderRadius: size * 0.34 }]}
        >
            <Ionicons name={icon} size={size * 0.5} color="#fff" />
        </LinearGradient>
    );
}

const styles = StyleSheet.create({
    otpRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    otpBox: {
        width: 42,
        height: 54,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: COLORS.line,
        backgroundColor: '#fff',
        textAlign: 'center',
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.ink,
    },
    otpBoxFilled: {
        borderColor: COLORS.brand,
        backgroundColor: COLORS.tint,
    },
    otpBoxFocused: {
        borderColor: COLORS.brand,
        borderWidth: 2,
    },
    otpBoxDisabled: {
        backgroundColor: COLORS.soft,
        color: COLORS.sub,
    },
    otpDash: {
        width: 12,
        height: 2,
        borderRadius: 2,
        backgroundColor: COLORS.line,
        marginHorizontal: 2,
    },
    ringCenter: {
        ...StyleSheet.absoluteFillObject,
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'center',
    },
    ringNumber: {
        fontSize: 24,
        fontWeight: '800',
        color: COLORS.ink,
    },
    ringUnit: {
        fontSize: 12,
        fontWeight: '700',
        color: COLORS.sub,
        marginLeft: 1,
    },
    resendBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: COLORS.brand,
        backgroundColor: COLORS.tint,
    },
    resendText: {
        color: COLORS.brandDark,
        fontWeight: '700',
        fontSize: 14,
    },
    rememberRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 20,
        backgroundColor: COLORS.soft,
        borderWidth: 1,
        borderColor: COLORS.line,
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 7,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
    },
    checkboxOn: {
        backgroundColor: COLORS.brand,
        borderColor: COLORS.brand,
    },
    rememberTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.ink,
    },
    rememberSub: {
        fontSize: 12,
        color: COLORS.sub,
        marginTop: 2,
    },
    brand: {
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: COLORS.brandDark,
        shadowOpacity: 0.35,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 6 },
        elevation: 6,
    },
});

